import React from 'react';
import styled from 'styled-components';
import { RebelColors } from '../../../../styles/rebel-experiences';

/**
 * Dropdown for ranking REBEL experiences
 * Prevents duplicate rankings across experiences
 * @param {string} experienceId - The experience ID this dropdown is for
 * @param {object} selectedRankings - All current rankings { expId: rank }
 * @param {function} onChange - Callback when ranking changes (expId, newRank)
 * @param {number} totalExperiences - Total number of experiences to rank
 */
const RankingDropdown = ({ experienceId, selectedRankings, onChange, totalExperiences }) => {

    const currentRank = selectedRankings[experienceId] || '';

    // Check if a rank is taken by another experience
    const isRankTaken = (rank) => {
        return Object.entries(selectedRankings).some(
            ([expId, selectedRank]) =>
                expId !== experienceId && parseInt(selectedRank) === parseInt(rank)
        );
    };

    const handleChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            onChange(experienceId, null);
        } else if (value === 'NA') {
            onChange(experienceId, 'NA');
        } else {
            onChange(experienceId, parseInt(value));
        }
    };

    return (
        <DropdownContainer>
            <RankingSelect
                value={currentRank}
                onChange={handleChange}
                hasSelection={currentRank !== ''}
                isNA={currentRank === 'NA'}
            >
                <option value="">Select Rank</option>
                {Array.from({ length: totalExperiences }, (_, i) => i + 1).map(rank => (
                    <option
                        key={rank}
                        value={rank}
                        disabled={isRankTaken(rank)}
                    >
                        {rank}{isRankTaken(rank) ? ' (taken)' : ''}
                    </option>
                ))}
                <option value="NA">N/A</option>
            </RankingSelect>
        </DropdownContainer>
    );
};

export default RankingDropdown;

const DropdownContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;

const RankingSelect = styled.select`
    width: 120px;
    padding: 7px 28px 7px 12px;
    margin: 0;
    border: 2px solid ${props => props.isNA ? RebelColors.danger : props.hasSelection ? RebelColors.gold : RebelColors.border};
    background-color: ${RebelColors.surface};
    color: ${RebelColors.text};
    border-radius: 6px;
    font-size: 0.9rem;
    line-height: 1.2;
    cursor: pointer;
    transition: all 0.3s ease;
    height: 38px;
    text-align: center;
    text-align-last: center;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fdb506' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 16px;

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
        box-shadow: 0 0 0 2px ${RebelColors.gold}22;
    }

    &:hover {
        border-color: ${RebelColors.gold};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    option {
        background-color: #2a2e34;
        color: ${RebelColors.text};
        padding: 8px 12px;
        text-align: center;
    }

    option:disabled {
        color: #888;
        font-style: italic;
    }

    /* Firefox-specific option centering */
    @-moz-document url-prefix() {
        option {
            text-align: center;
        }
    }
`;
